import { Component,
  OnInit,
  AfterViewInit,
  AfterViewChecked,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { DSTSharedService } from '../shared/dst-shared.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { StatementsFiledStatusService } from './statements-filed-status.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../shared/workspace-element-context';
import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../shared/utils/dst-tax-year-appkit.support';
import {
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../shared/utils/appkit-dropdown.util';
import { DstMatTableDataSource } from '../shared/mat-table/dst-mat-table-data-source';
import { DstMatTableFilterApi } from '../shared/mat-table/dst-mat-table-filter-api';
import {
  buildDisplayedColumns,
  connectMatTableSort,
  getDstColStyle,
} from '../shared/mat-table/dst-mat-grid.util';

/** Which action button is currently updating (for per-button loader). */
export type StatementsFiledStatusAction = 'markAsFiled' | 'markAsNotFiled' | 'markQarAsY' | 'markQarAsN' | null;

/** Row from statements filed status grid (SELECTED is used for bulk actions). */
export interface StatementsFiledStatusRow {
  STATEMENTID: number | string;
  PRIMARYLEID?: string;
  SECONDARYLEID?: string;
  BRANCHLEID?: string;
  STATEMENTNAME?: string;
  ISQAR?: number | string;
  FILEDSTATUS?: string;
  CREATEDBY?: string;
  SELECTED?: boolean;
}

/**
 * DST Statements Filed Status screen.
 * Tax year and business dropdowns; grid with Select All/Clear All and Mark As Filed / Not Filed / QAR Y/N.
 */
@Component({
    selector: 'gtw-dst-statements-filed-status',
    templateUrl: './statements-filed-status.component.html',
    styleUrls: [
      '../shared/mat-table/dst-mat-table.scss',
      './statements-filed-status.component.scss',
    ],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class StatementsFiledStatusComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @ViewChild('tableWrapper') tableWrapper!: ElementRef<HTMLElement>;
  @ViewChild(MatSort) sort!: MatSort;

  /** True when table body has vertical scroll (used to add header padding for border alignment). */
  tableBodyHasScroll = false;

  private scrollCheckScheduled = false;

  taxYears: { text: string }[] = [];
  businesses: { text: string; id: string | number }[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  businessItems: any[] = [];
  selectedTaxYear: { text: string } | null = null;
  selectedBusiness: { text: string; id: string | number } | null = null;
  selectedTaxYearItem: any = null;
  selectedBusinessItem: any = null;
  gridLoading = false;
  statementFiledStatusUpdating = false;
  /** Which action is in progress (loader shown only on that button). */
  updatingAction: StatementsFiledStatusAction = null;
  data: StatementsFiledStatusRow[] = [];
  dataSource = new DstMatTableDataSource<StatementsFiledStatusRow>([]);
  table: DstMatTableFilterApi<StatementsFiledStatusRow>;
  displayedColumns: string[] = [];
  taxYearLoaded = false;
  /** Avoid running init twice when param is set (e.g. on redirect). */
  private hasAppliedGtwParam = false;

  /** Global search text (aligned with old gt-datatable2 searching: true). */
  searchText = '';

  /** Table column definitions for mat-table. */
  cols = [
    { field: 'SELECTED', header: 'Select', width: '60px' },
    { field: 'STATEMENTID', header: 'Statement Id', width: '120px' },
    { field: 'PRIMARYLEID', header: 'Primary LEID', width: '120px' },
    { field: 'SECONDARYLEID', header: 'Secondary LEID', width: '130px' },
    { field: 'BRANCHLEID', header: 'Branch LEID', width: '110px' },
    { field: 'STATEMENTNAME', header: 'Statement Name', width: '300px' },
    { field: 'ISQAR', header: 'Is QAR', width: '80px' },
    { field: 'FILEDSTATUS', header: 'Filed Status', width: '110px' },
    { field: 'CREATEDBY', header: 'Created By', width: '130px' },
  ];

  /** True when all rows are selected (for header checkbox). */
  get allSelected(): boolean {
    return this.data.length > 0 && this.data.every((r) => r.SELECTED);
  }

  /** True when some but not all rows selected (header checkbox indeterminate). */
  get indeterminateSelected(): boolean {
    const selected = this.data.filter((r) => r.SELECTED).length;
    return selected > 0 && selected < this.data.length;
  }

  /** Handle header select-all checkbox change. */
  onSelectAllChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.checkUncheckAll(checked);
  }

  /** Handle row checkbox change. */
  onRowSelectChange(row: StatementsFiledStatusRow, event: Event): void {
    this.rowSelect(row, (event.target as HTMLInputElement).checked);
  }

  constructor(
    public dstShared: DSTSharedService,
    private service: StatementsFiledStatusService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {
    this.displayedColumns = buildDisplayedColumns(this.cols);
    this.dataSource.globalFilterFields = [
      'STATEMENTID',
      'PRIMARYLEID',
      'SECONDARYLEID',
      'BRANCHLEID',
      'STATEMENTNAME',
      'FILEDSTATUS',
      'CREATEDBY',
    ];
    this.table = new DstMatTableFilterApi(this.dataSource);
  }

  private readonly bodyScrollLockClass = 'dst-statements-no-page-scroll';

  ngOnInit(): void {
    document.body.classList.add(this.bodyScrollLockClass);
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    this.connectSort();
  }

  private connectSort(): void {
    connectMatTableSort(this.dataSource, this.sort);
  }

  ngOnDestroy(): void {
    document.body.classList.remove(this.bodyScrollLockClass);
    this.subs.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (this.scrollCheckScheduled || !this.selectedTaxYear || !this.selectedBusiness || this.gridLoading) return;
    this.scrollCheckScheduled = true;
    setTimeout(() => {
      this.connectSort();
      this.checkTableBodyScroll();
      this.scrollCheckScheduled = false;
    }, 0);
  }

  /** Update tableBodyHasScroll so header padding is only applied when table body has a scrollbar. */
  private checkTableBodyScroll(): void {
    const el = this.tableWrapper?.nativeElement?.querySelector('.dst-mat-grid') as HTMLElement | null;
    const hasScroll = el ? el.scrollHeight > el.clientHeight : false;
    if (hasScroll !== this.tableBodyHasScroll) {
      this.tableBodyHasScroll = hasScroll;
      this.cdr.detectChanges();
    }
  }

  /** Re-check scroll when window is resized so scrollbar/header padding stay correct. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.tableWrapper?.nativeElement && this.selectedTaxYear && this.selectedBusiness && !this.gridLoading) {
      setTimeout(() => this.checkTableBodyScroll(), 50);
    }
  }

  /** Apply external search filter to mat-table data source. */
  onSearchChange(): void {
    this.table.filterGlobal(this.searchText, 'contains');
    setTimeout(() => this.checkTableBodyScroll(), 0);
  }

  getColByField(field: string): { field: string; header: string; width: string } {
    return this.cols.find((c) => c.field === field) || { field, header: field, width: '100px' };
  }

  getColStyle(col: { width?: string }): Record<string, string> {
    return getDstColStyle(col);
  }

  private syncDataSource(): void {
    this.dataSource.data = this.data;
    this.connectSort();
    this.cdr.markForCheck();
  }

  private applyTaxYearSelection(selected: any): void {
    if (!selected) {
      return;
    }
    this.selectedTaxYearItem = selected;
    const label = String(selected?.label ?? selected?.text ?? '');
    if (!label) {
      return;
    }
    // Avoid double load when AppKit fires both onSelect and onSelectItem.
    if (this.selectedTaxYear?.text === label) {
      return;
    }
    this.selectedTaxYear =
      this.taxYears.find((ty) => ty.text === label) || { text: label };
    this.onTaxYearChange();
  }

  private applyBusinessSelection(selected: any): void {
    if (!selected) {
      return;
    }
    this.selectedBusinessItem = selected;
    const selectedValue = selected?.value ?? selected?.id;
    const selectedLabel = String(selected?.label ?? selected?.text ?? '');
    const matched =
      this.businesses.find((b) => String(b.id) === String(selectedValue)) ||
      this.businesses.find((b) => b.text === selectedLabel) ||
      null;
    if (!matched) {
      return;
    }
    // Avoid double load when AppKit fires both onSelect and onSelectItem.
    if (
      this.selectedBusiness &&
      String(this.selectedBusiness.id) === String(matched.id)
    ) {
      return;
    }
    this.selectedBusiness = matched;
    this.onBusinessChange();
  }

  private applyWorkspaceContext(): void {
    if (this.hasAppliedGtwParam) return;
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) return;

      this.hasAppliedGtwParam = true;
      this.dstShared.setClientKey(clientKey);
      this.dstShared.setTaxYearScenario(taxYear, scenario);
      this.dstShared.setBaseURLs(gc);
      this.dstShared.setDSTUser(ssoId, roleLevel);
      this.taxYearLoaded = true;
      this.loadTaxYears();
    } catch {
      // Context not yet available; skip.
    }
  }

  /** Load tax years; no value selected at first (user must choose). */
  loadTaxYears(): void {
    this.service.getTaxYears().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        const raw = list || [];
        this.taxYears = raw.map((t: any) => ({ text: String(t.text ?? t.TAX_YEAR ?? t.value ?? t) }));
        this.taxYearItems = this.taxYears.map((ty, index) => ({ value: index, label: ty.text }));
        this.selectedTaxYear = null;
        this.selectedTaxYearItem = null;
      },
      error: () => this.dstShared.showAlert('error', 'Failed to load tax years.'),
    });
  }

  /** On tax year change: load businesses and clear grid. */
  onTaxYearChange(): void {
    this.searchText = '';
    this.table.filterGlobal('', 'contains');
    this.selectedBusiness = null;
    this.selectedBusinessItem = null;
    this.data = [];
    this.syncDataSource();
    const taxYear = this.selectedTaxYear?.text;
    if (!taxYear) return;
    this.service.getBusinesses(taxYear).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        const raw = list || [];
        this.businesses = raw.map((b: any) => ({
          text: b.text ?? b.BUSINESS_NAME ?? b.name ?? String(b.id ?? b.BUSINESS_ID ?? ''),
          id: b.id ?? b.BUSINESS_ID ?? b.value ?? ''
        }));
        this.businessItems = mapToAppkitDropdownItems(this.businesses, 'text', 'id');
        this.selectedBusinessItem = null;
      },
      error: () => this.dstShared.showAlert('error', 'Failed to load businesses.'),
    });
  }

  onTaxYearDropdownChange(event: any): void {
    this.applyTaxYearSelection(getAppkitDropdownSelection(event));
  }

  onBusinessDropdownChange(event: any): void {
    this.applyBusinessSelection(getAppkitDropdownSelection(event));
  }

  onTaxYearSelectItem(event: any): void {
    // AppKit often fires onSelectItem without onSelect — drive the same load path.
    this.applyTaxYearSelection(getAppkitDropdownSelection(event));
  }

  onBusinessSelectItem(event: any): void {
    this.applyBusinessSelection(getAppkitDropdownSelection(event));
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  /** On business change: load grid data. */
  onBusinessChange(): void {
    this.searchText = '';
    this.table.filterGlobal('', 'contains');
    const taxYear = this.selectedTaxYear?.text;
    const businessId = this.selectedBusiness?.id;
    if (!taxYear || businessId == null || businessId === '') {
      this.data = [];
      this.syncDataSource();
      return;
    }
    this.gridLoading = true;
    this.cdr.detectChanges();
    this.service.getGridData(taxYear, businessId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows) => {
        this.data = (rows || []).map((r) => ({ ...r, SELECTED: false }));
        this.syncDataSource();
        this.gridLoading = false;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.connectSort();
          this.checkTableBodyScroll();
        }, 100);
      },
      error: () => {
        this.dstShared.showAlert('error', 'Failed to load grid.');
        this.gridLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  /** Set all rows SELECTED to true or false. */
  checkUncheckAll(checked: boolean): void {
    this.data.forEach((row) => (row.SELECTED = checked));
  }

  /** Toggle row SELECTED. */
  rowSelect(row: StatementsFiledStatusRow, checked: boolean): void {
    row.SELECTED = checked;
  }

  /** Comma-separated list of statement IDs for selected rows. */
  getStatementIdList(): string {
    return this.data
      .filter((r) => r.SELECTED)
      .map((r) => r.STATEMENTID)
      .join(',');
  }

  /** Success message text for current action (matches AngularJS vm.successMsg). */
  private successMsg = '';

  /** Mark selected as Filed (FILED_STATUS = 1). */
  markAsFiled(): void {
    this.successMsg = 'Marked As Filed';
    this.updateStatementFiledStatus('1', 'FILED_STATUS', 'markAsFiled');
  }

  /** Mark selected as Not Filed (FILED_STATUS = 0). */
  markAsNotFiled(): void {
    this.successMsg = 'Marked As Not Filed';
    this.updateStatementFiledStatus('0', 'FILED_STATUS', 'markAsNotFiled');
  }

  /** Mark selected QAR as Y (FILED_QAR_STATUS = 1). */
  markQarAsY(): void {
    this.successMsg = 'Marked QAR As Y';
    this.updateStatementFiledStatus('1', 'FILED_QAR_STATUS', 'markQarAsY');
  }

  /** Mark selected QAR as N (FILED_QAR_STATUS = 0). */
  markQarAsN(): void {
    this.successMsg = 'Marked QAR As N';
    this.updateStatementFiledStatus('0', 'FILED_QAR_STATUS', 'markQarAsN');
  }

  /**
   * Call update API and reload grid on success.
   */
  private updateStatementFiledStatus(inStatusType: string, inType: string, action: StatementsFiledStatusAction): void {
    const ids = this.getStatementIdList();
    if (!ids) {
      this.gtwSnackbarService.openSnackBar('Please select at least one statement.', 'gtw-snackbar--yellow');
      return;
    }
    this.statementFiledStatusUpdating = true;
    this.updatingAction = action;
    this.service.updateStatementFiledStatus(ids, inStatusType, inType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.statementFiledStatusUpdating = false;
        this.updatingAction = null;
        if (res?.callSuccess === '1' && res?.errorMessage === 'null') {
          this.gtwSnackbarService.openSnackBar(`Statement ${this.successMsg} Successfully!`, 'gtw-snackbar--green', true, 4);
          this.onBusinessChange();
        } else {
          this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Update failed.', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.statementFiledStatusUpdating = false;
        this.updatingAction = null;
        this.gtwSnackbarService.openSnackBar('Update failed.', 'gtw-snackbar--red');
      },
    });
  }

  /** Display value for Is QAR: 0 -> N, else Y. */
  isQarDisplay(row: StatementsFiledStatusRow): string {
    const v = row.ISQAR;
    return v === 0 || v === '0' ? 'N' : 'Y';
  }

}
